import { z } from "zod";

/**
 * Profile form validation schema with comprehensive business rules
 * Following RFC 5322 for email validation and practical name requirements
 */
export const profileFormSchema = z.object({
  firstName: z
    .string()
    .min(1, "First name is required")
    .min(2, "First name must be at least 2 characters")
    .max(50, "First name cannot exceed 50 characters")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "First name can only contain letters, spaces, hyphens, and apostrophes",
    )
    .refine(
      (val) => val.trim().length > 0,
      "First name cannot be only whitespace",
    )
    .transform((val) => val.trim()),

  lastName: z
    .string()
    .min(1, "Last name is required")
    .min(2, "Last name must be at least 2 characters")
    .max(50, "Last name cannot exceed 50 characters")
    .regex(
      /^[a-zA-Z\s'-]+$/,
      "Last name can only contain letters, spaces, hyphens, and apostrophes",
    )
    .refine(
      (val) => val.trim().length > 0,
      "Last name cannot be only whitespace",
    )
    .transform((val) => val.trim()),

  email: z
    .string()
    .min(1, "Email address is required")
    .email("Please enter a valid email address")
    .max(254, "Email address is too long") // RFC 5321 limit
    .toLowerCase()
    .refine(
      (val) => !val.includes(".."),
      "Email cannot contain consecutive dots",
    )
    .refine(
      (val) => !val.startsWith(".") && !val.endsWith("."),
      "Email cannot start or end with a dot",
    ),
});

export type ProfileFormData = z.infer<typeof profileFormSchema>;

/**
 * Type for tracking which fields have changed
 */
export type ProfileChanges = {
  hasNameChanges: boolean;
  hasEmailChanges: boolean;
  hasAnyChanges: boolean;
  changedFields: Array<keyof ProfileFormData>;
};

/**
 * Original profile data for comparison
 */
export type OriginalProfileData = {
  firstName: string;
  lastName: string;
  email: string;
};
